import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import {
    PanelAccess,
    employeeCanAccessPath,
    firstAllowedPath,
    isActivePanelEmployee,
} from '../../auth/adminAccess';

interface PrivateRouteProps {
    children: React.ReactNode;
}

function accessFromData(data: Record<string, unknown> | undefined): PanelAccess {
    const permissions = data?.permissions;
    return {
        status: typeof data?.status === 'string' ? data.status : undefined,
        enabled: typeof data?.enabled === 'boolean' ? data.enabled : undefined,
        permissions: Array.isArray(permissions) ? permissions.filter((p): p is string => typeof p === 'string') : [],
        superAdmin: data?.superAdmin === true,
    };
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
    const location = useLocation();
    const { authState, currentUser } = useAuth();
    const [access, setAccess] = useState<PanelAccess | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkEmployeeStatus = async () => {
            if (!currentUser) {
                setAccess(null);
                setLoading(false);
                return;
            }

            try {
                const employeeDocRef = doc(db, 'employees', currentUser.uid);
                const employeeDoc = await getDoc(employeeDocRef);
                setAccess(employeeDoc.exists() ? accessFromData(employeeDoc.data()) : null);
            } catch (error) {
                console.error('Error checking employee status:', error);
                // Don't clear a previously verified employee on a transient network error.
            }

            setLoading(false);
        };

        if (authState.isAuthenticated && currentUser) {
            checkEmployeeStatus();
        } else if (!authState.loading) {
            setAccess(null);
            setLoading(false);
        }
    }, [currentUser, authState]);

    if (authState.loading || loading) {
        return null;
    }

    if (!authState.isAuthenticated) {
        return <Navigate to="/login" state={{ from: location.pathname }} />;
    }

    if (!isActivePanelEmployee(access) || !employeeCanAccessPath(access, location.pathname)) {
        const fallback = firstAllowedPath(access);
        if (fallback && fallback !== location.pathname) {
            return <Navigate to={fallback} replace />;
        }
        return <Navigate to="/login" state={{ from: location.pathname }} />;
    }

    return <>{children}</>;
};

export default PrivateRoute;
